/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'reactstrap';
import * as default_data from '../config.js';
import { getToken } from '../AuthService.js';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TextField, FormGroup, FormLabel, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper } from '@mui/material';
import { FiEdit } from 'react-icons/fi';
import { IoCardOutline } from "react-icons/io5";
import { FaUserPlus } from 'react-icons/fa';
import { getBannerColor } from '../Utils/classification_helper.js';
import { sanitizeHtml } from '../Utils/text_functions';

import AdminHorizontalMenu from '../Admin/AdminHorizontalMenu.js';

function SuperAdminOrganizationsList() {
  const [organizations, setOrganizations] = useState([]);
  const [errorMessageAdd, setErrorMessageAdd] = useState('');
  const [errorMessageUpdate, setErrorMessageUpdate] = useState('');
  const [errorMessageAssignUsers, setErrorMessageAssignUsers] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [openAssignUsersDialog, setOpenAssignUsersDialog] = useState(false);
  const [organization, setOrganization] = useState({
    name: '',
    point_contact: '',
    address: '',
    zip: '',
    city: '',
    country: '',
    phone: '',
    email: '',
    domainname: '',
    force_models: ''
  });
  const [emails, setEmails] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("Getting things ready...");
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');
  const [uploadSuccessMessage, setUploadSuccessMessage] = useState('');
  const loading_styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.9)', // Adjust the opacity as needed
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999, // Adjust the z-index as needed to ensure it appears above other elements
    },
    loadingText: {
      color: '#fff',
      fontSize: '24px',
    },
  };

  const [organizationId, setOrganizationId] = useState(null);

  var loaded_static = false;

  function loadOrganizations() {
    setIsLoading(true);
    var token = getToken();
    if (token === false) {
      return;
    }

    fetch(default_data.default_user_service_url_no_cac + '/superadmin-get-all-organizations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({})
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while getting data.');
      })
      .then(data => {
        if (data.status != 200) {
          window.location = '/login';
          return;
        }
        data = data.response;
        setOrganizations(data);
        setIsLoading(false);
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
      });
  }

  useEffect(() => {
    if (loaded || loaded_static) {
      return;
    }

    setLoaded(true);
    loaded_static = true;

    loadOrganizations();
  }, []);

  const handleChange = (event) => {
    setOrganization({ ...organization, [event.target.name]: event.target.value });
  };

  const handleEmailsChange = (event) => {
    setEmails(event.target.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (openAddDialog) {
      addOrganization(organization);
    } else if (openUpdateDialog) {
      updateOrganization(organization);
    } else if (openAssignUsersDialog) {
      assignUsersToOrganization(emails);
    }
  };

  function addOrganization(organization) {
    setErrorMessageAdd('');
    var token = getToken();
    if (token === false) {
      return;
    }

    fetch(default_data.default_user_service_url_no_cac + '/superadmin-add-organization', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify(organization)
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while adding the organization.');
      })
      .then(data => {
        if (data.status != 200) {
          throw new Error(data.response);
        }
        loadOrganizations();
        setOpenAddDialog(false);
      })
      .catch(error => {
        setErrorMessageAdd(error.toString());
      });
  }

  function updateOrganization(organization) {
    setErrorMessageUpdate('');
    if (typeof organizationId !== 'number') {
      console.log('Invalid organization_id');
      return;
    }

    var token = getToken();
    if (token === false) {
      return;
    }

    fetch(default_data.default_user_service_url_no_cac + '/superadmin-update-organization/' + organizationId, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify(organization)
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while updating the organization.');
      })
      .then(data => {
        if (data.status != 200) {
          window.location = '/login';
          return;
        }
        loadOrganizations();
        setOpenUpdateDialog(false);
      })
      .catch(error => {
        setErrorMessageUpdate(error.toString());
      });
  }

  function assignUsersToOrganization(emails) {
    setErrorMessageAssignUsers('');
    if (typeof organizationId !== 'number') {
      console.log('Invalid organization_id');
      return;
    }

    var token = getToken();
    if (token === false) {
      return;
    }

    if(emails === '') {
      setErrorMessageAssignUsers('Please enter at least one email address.');
      return;
    }
    var emailArray = [];
    if(emails.indexOf('\n') === -1) {
      emailArray = [emails.trim()];
    }
    else {
      if(emails.split('\n').length > 100) {
        setErrorMessageAssignUsers('You can only assign up to 100 users at a time.');
        return;
      }

      emailArray = emails.split('\n').map(email => email.trim()).filter(email => email);
    }

    fetch(default_data.default_user_service_url_no_cac + '/superadmin-assign-users-to-organization', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({ organization_id: organizationId, emails: emailArray })
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while assigning users to the organization.');
      })
      .then(data => {
        if (data.status != 200) {
          throw new Error(data.response);
        }
        loadOrganizations();
        setOpenAssignUsersDialog(false);
      })
      .catch(error => {
        setErrorMessageAssignUsers(error.toString());
      });
  }

  const openOrganizationPurchase = (organization) => {
    window.open('/superadmin/organization-purchase/' + organization.id, '_blank');
  };

  const openUpdateDialogWithOrganization = (organization) => {
    setOrganization(organization);
    setOrganizationId(organization.id);
    setOpenUpdateDialog(true);
  };

  const openAssignUsersDialogWithOrganization = (organization) => {
    setOrganizationId(organization.id);
    setOpenAssignUsersDialog(true);
  };

  const findOrganizationName = (organization_id) => {
    if(organization_id === null) {
      return;
    }
    const organization = organizations.find(organization => organization.id === organization_id);
    return organization ? organization.name : '';
  }

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleFileUpload = () => {
    if (!selectedFile) {
      setUploadErrorMessage('Please select a file to upload.');
      return;
    }

    const token = getToken();
    if (!token) {
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    fetch(default_data.default_user_service_url_no_cac + '/superadmin-update-sales', {
      method: 'POST',
      headers: {
        'x-access-tokens': `${token}`,
      },
      body: formData,
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while uploading the file.');
      })
      .then((data) => {
        if (data.status !== 200) {
          throw new Error(data.response);
        }
        setUploadSuccessMessage(data.response); // Display success message
        setUploadErrorMessage('');
      })
      .catch((error) => {
        setUploadErrorMessage(error.toString());
        setUploadSuccessMessage('');
      });
  };

  const DisplayData = organizations.map(
    (info) => {
      return (
        <TableRow key={info.id}>
          <TableCell className="compact-cell"><a href={`/superadmin/get-users?organization_id=${info.id}`} target="_blank">{info.id}</a></TableCell>
          <TableCell className="compact-cell">
            <IconButton title="Manage Org Purchases" aria-label="Manage Org Purchases" variant="contained" onClick={() => openOrganizationPurchase(info)}>
                <IoCardOutline />
            </IconButton>
            <IconButton title="Update Org" aria-label="Update Org" variant="contained" onClick={() => openUpdateDialogWithOrganization(info)}>
                <FiEdit />
            </IconButton>
            <IconButton title="Assign Users" aria-label="Assign Users" variant="contained" onClick={() => openAssignUsersDialogWithOrganization(info)}>
                <FaUserPlus />
            </IconButton>
          </TableCell>
          <TableCell className="compact-cell">{info.name}</TableCell>
          <TableCell className="compact-cell">{info.point_contact}</TableCell>
          <TableCell className="compact-cell">{info.phone}</TableCell>
          <TableCell className="compact-cell">{info.email}</TableCell>
          <TableCell className="compact-cell">{info.domainname}</TableCell>
          <TableCell className="compact-cell">{navigator.language.includes("en") || navigator.language.includes("US") || navigator.language.includes("us")
? info.purchased_total_tokens.toLocaleString(navigator.language)
: info.purchased_total_tokens} </TableCell>
          <TableCell className="compact-cell">{navigator.language.includes("en") || navigator.language.includes("US") || navigator.language.includes("us")
? info.purchased_total_train_tokens.toLocaleString(navigator.language)
: info.purchased_total_train_tokens} </TableCell>

          <TableCell className="compact-cell">{navigator.language.includes("en") || navigator.language.includes("US") || navigator.language.includes("us")
? info.used_total_tokens.toLocaleString(navigator.language)
: info.used_total_tokens} </TableCell>

          <TableCell className="compact-cell">{navigator.language.includes("en") || navigator.language.includes("US") || navigator.language.includes("us")
? info.used_total_train_tokens.toLocaleString(navigator.language)
: info.used_total_train_tokens} </TableCell>
        </TableRow>
      );
    }
  );

  return (
    <div>
      <div className={`header-message ${getBannerColor(true)}`}>
        {default_data.default_banner_override != null && default_data.default_banner_override !== '' ?
          default_data.default_banner_override : 'CUI'
        }
      </div>
      <div className="reg-page-mode">
        <Container style={{ maxWidth: '100%' }}>
          <Row className="justify-content-center reg-page-mode-header">
            <Col xs="12" md="12" lg="12" className="d-flex align-items-center">
              <a href="/" className="d-flex align-items-center">
                <img src="/logo.png" alt="Ask Sage" className="logo" />
              </a>
              <h1 className="user-list-h1">
                Organizations
              </h1>
            </Col>
          </Row>
          <Row className="user-list-reg-page-mode-content justify-content-center reg-page-mode-content" style={{ padding: '10px' }}>
            <Col xs="12" md="12" lg="12">
              <AdminHorizontalMenu type={'superadmin'} full_stats={0} />
              <br /><br />
              <Button variant="contained" onClick={() => setOpenAddDialog(true)}>Add new Organization</Button>
              <Button variant="contained" onClick={() => setOpenUploadDialog(true)} style={{ marginLeft: '10px' }}>
                Superadmin: Upload Excel Sales File
              </Button>
              <div className="user-list-table-parent">
                <TableContainer component={Paper}>
                  <Table className="user-list-table">
                    <TableHead>
                      <TableRow>
                        <TableCell className="compact-cell">ID</TableCell>
                        <TableCell className="compact-cell">Actions</TableCell>
                        <TableCell className="compact-cell">Organization</TableCell>
                        <TableCell className="compact-cell">Contact</TableCell>
                        <TableCell className="compact-cell">Phone</TableCell>
                        <TableCell className="compact-cell">Email</TableCell>
                        <TableCell className="compact-cell">Domain</TableCell>
                        <TableCell className="compact-cell">Purchased Tokens</TableCell>
                        <TableCell className="compact-cell">Purchased Train Tokens</TableCell>
                        <TableCell className="compact-cell">Assigned Tokens</TableCell>
                        <TableCell className="compact-cell">Assigned Train Tokens</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {DisplayData.length > 0 ? DisplayData : <TableRow><TableCell colSpan={11}>Loading...</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </Col>
          </Row>
          <Row className="justify-content-center reg-page-mode-footer">
            <Col xs="12" md="12" lg="12">
              Copyright &copy; {default_data.default_copywrite_year} Ask Sage, Inc.
            </Col>
          </Row>
        </Container>
      </div>

      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        aria-labelledby="form-dialog-title"
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle id="form-dialog-title">Add New Organization</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <FormLabel>Name*</FormLabel>
              <TextField type="text" name="name" onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Point Contact*</FormLabel>
              <TextField type="text" name="point_contact" onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Address</FormLabel>
              <TextField type="text" name="address" onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Zip</FormLabel>
              <TextField type="text" name="zip" onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>City</FormLabel>
              <TextField type="text" name="city" onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Country*</FormLabel>
              <TextField type="text" name="country" onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Phone</FormLabel>
              <TextField type="text" name="phone" onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Email*</FormLabel>
              <TextField type="text" name="email" onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Domain Name (used for auto assigning user with domain to org)</FormLabel>
              <TextField type="text" name="domainname" onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Force models (leave empty if no limit in models)</FormLabel>
              <TextField type="text" name="force_models" onChange={handleChange} fullWidth />
            </FormGroup>
            {errorMessageAdd && <p style={{ color: 'red' }}>{errorMessageAdd}</p>}
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSubmit} color="primary">
            Submit
          </Button>
          <Button onClick={() => setOpenAddDialog(false)} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openUpdateDialog}
        onClose={() => setOpenUpdateDialog(false)}
        aria-labelledby="form-dialog-title"
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle id="form-dialog-title">Update Organization</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <FormLabel>Name*</FormLabel>
              <TextField type="text" name="name" value={organization.name} onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Point Contact*</FormLabel>
              <TextField type="text" name="point_contact" value={organization.point_contact} onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Address</FormLabel>
              <TextField type="text" name="address" value={organization.address} onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Zip</FormLabel>
              <TextField type="text" name="zip" value={organization.zip} onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>City</FormLabel>
              <TextField type="text" name="city" value={organization.city} onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Country*</FormLabel>
              <TextField type="text" name="country" value={organization.country} onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Phone</FormLabel>
              <TextField type="text" name="phone" value={organization.phone} onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Email*</FormLabel>
              <TextField type="text" name="email" value={organization.email} onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Domain Name (used for auto assigning user with domain to org)</FormLabel>
              <TextField type="text" name="domainname" value={organization.domainname} onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Force models (leave empty if no limit in models)</FormLabel>
              <TextField type="text" name="force_models" value={organization.force_models} onChange={handleChange} fullWidth />
            </FormGroup>
            {errorMessageUpdate && <p style={{ color: 'red' }}>{errorMessageUpdate}</p>}
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSubmit} color="primary">
            Submit
          </Button>
          <Button onClick={() => setOpenUpdateDialog(false)} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAssignUsersDialog}
        onClose={() => setOpenAssignUsersDialog(false)}
        aria-labelledby="form-dialog-title"
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle id="form-dialog-title">Assign Users to Organization {findOrganizationName(organizationId)}</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <FormLabel>Emails (one per line)*</FormLabel>
              <TextField
                type="text"
                name="emails"
                value={emails}
                onChange={handleEmailsChange}
                fullWidth
                multiline
                rows={10}
              />
            </FormGroup>
            {errorMessageAssignUsers && <p style={{ color: 'red' }}>{errorMessageAssignUsers}</p>}
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSubmit} color="primary">
            Submit
          </Button>
          <Button onClick={() => setOpenAssignUsersDialog(false)} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openUploadDialog}
        onClose={() => {
          setOpenUploadDialog(false);
          setUploadErrorMessage('');
          setUploadSuccessMessage('');
          setSelectedFile(null);
        }}
        aria-labelledby="form-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="form-dialog-title">Upload Sales Data</DialogTitle>
        <DialogContent>
          <FormGroup>
            <FormLabel>Select Excel File (ensure you use Ask Sage template provided)*</FormLabel>
            <TextField
              type="file"
              onChange={handleFileChange}
              fullWidth
              inputProps={{ accept: '.xlsx' }}
            />
          </FormGroup>
          {uploadErrorMessage && <p style={{ color: 'red' }}>{uploadErrorMessage}</p>}
          {uploadSuccessMessage && (
            <p
              style={{ color: 'green' }}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(uploadSuccessMessage.replace(/\n/g, '<br />'), ['br']),
              }}
            ></p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFileUpload} color="primary">
            Upload
          </Button>
          <Button
            onClick={() => {
              setOpenUploadDialog(false);
              setUploadErrorMessage('');
              setUploadSuccessMessage('');
              setSelectedFile(null);
            }}
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading overlay */}
      {isLoading && (
        <div style={loading_styles.overlay}>
          <span style={loading_styles.loadingText}>{loadingText}</span>
        </div>
      )}
    </div>
  );
}

export default SuperAdminOrganizationsList;